import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Tipo de role do aplicativo (inclui tenant_supervisor)
type AppRole = 'root_admin' | 'tenant_admin' | 'tenant_supervisor' | 'agent' | 'user';

interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  tenant_id: string | null;
  created_at: string;
  profile?: {
    email: string;
    full_name: string | null;
  };
}

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
}

// Interface para dados do tenant
interface Tenant {
  id: string;
  name: string;
  slug: string;
}

// Descrições dos roles para exibição
const roleDescriptions: Record<AppRole, string> = {
  root_admin: 'Acesso total ao sistema',
  tenant_admin: 'Admin de uma organização',
  tenant_supervisor: 'Supervisor com permissões configuráveis',
  agent: 'Agente de atendimento',
  user: 'Usuário comum',
};

// Cores dos badges por role
const getRoleBadgeColor = (role: string) => {
  switch (role) {
    case 'root_admin':
      return 'bg-red-500/10 text-red-600 border-red-500/20';
    case 'tenant_admin':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'tenant_supervisor':
      return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
    case 'agent':
      return 'bg-green-500/10 text-green-600 border-green-500/20';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
};

const AdminRoles = () => {
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [tenantsMap, setTenantsMap] = useState<Map<string, Tenant>>(new Map());
  const [loading, setLoading] = useState(true);
  // Estado do dialog removido temporariamente
  // const [dialogOpen, setDialogOpen] = useState(false);
  // const [newRole, setNewRole] = useState({
  //   user_id: '',
  //   role: 'user' as AppRole,
  // });
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Busca roles, profiles e tenants em paralelo
      const [rolesResult, profilesResult, tenantsResult] = await Promise.all([
        supabase.from('user_roles').select('*').order('created_at', { ascending: false }),
        supabase.from('profiles').select('id, email, full_name'),
        supabase.from('tenants').select('id, name, slug'),
      ]);

      if (rolesResult.error) throw rolesResult.error;
      if (profilesResult.error) throw profilesResult.error;
      if (tenantsResult.error) throw tenantsResult.error;

      // Mapa de profiles para lookup rápido
      const profilesMap = new Map(
        (profilesResult.data || []).map((p) => [p.id, p])
      );

      // Mapa de tenants para lookup rápido
      const tenantMap = new Map<string, Tenant>(
        (tenantsResult.data || []).map((t) => [t.id, t])
      );

      const rolesWithProfiles = (rolesResult.data || []).map((role) => ({
        ...role,
        profile: profilesMap.get(role.user_id),
      }));

      setRoles(rolesWithProfiles);
      setProfiles(profilesResult.data || []);
      setTenantsMap(tenantMap);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os dados',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Função de criação removida temporariamente
  // const createRole = async () => { ... };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Permissões</h1>
          <p className="text-muted-foreground">Gerencie as roles dos usuários</p>
        </div>
        {/* Botão "Atribuir Role" ocultado temporariamente */}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Roles Atribuídas
          </CardTitle>
          <CardDescription>
            {roles.length} roles atribuídas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground">Carregando...</p>
          ) : roles.length === 0 ? (
            <p className="text-muted-foreground">Nenhuma role atribuída</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Tenant</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {role.profile?.full_name || 'Sem nome'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {role.profile?.email}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getRoleBadgeColor(role.role)}>
                        {role.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {role.tenant_id ? (
                        <div>
                          <p className="font-medium text-foreground">
                            {tenantsMap.get(role.tenant_id)?.name || 'Tenant desconhecido'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {tenantsMap.get(role.tenant_id)?.slug || role.tenant_id.substring(0, 8)}
                          </p>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Global</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminRoles;
